/* Username + profile picture editor (avatar resized client-side to 256px JPEG). */
import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Camera, Check, X, AtSign, Trash2, Share2, Copy, ExternalLink } from "lucide-react";

function PublicCardSettings({ user }) {
  const [st, setSt] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/profile/public-settings").then(({ data }) => setSt(data.settings)).catch(() => {}); }, []);
  const update = async (patch) => {
    setErr(""); setMsg("");
    try { const { data } = await api.patch("/profile/public-settings", patch); setSt(data.settings); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };
  if (!st) return null;
  const url = user?.username ? `${window.location.origin}/u/${user.username}` : null;
  const Toggle = ({ k, label, testid }) => (
    <label className="flex items-center justify-between gap-3 text-sm py-1.5 cursor-pointer">
      <span className="text-ink">{label}</span>
      <input type="checkbox" checked={!!st[k]} onChange={(e) => update({ [k]: e.target.checked })} className="h-4 w-4 accent-forest" data-testid={testid} />
    </label>
  );
  return (
    <div className="mt-6 pt-6 border-t border-sand" data-testid="public-card-settings">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage inline-flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5" /> Shareable profile card</div>
      <p className="text-xs text-moss mt-1">A public page at <span className="font-mono">/u/{user?.username || "your-username"}</span> showing only what you tick below.</p>
      <div className="mt-3 max-w-sm">
        <Toggle k="enabled" label="Make my card public" testid="public-toggle-enabled" />
        <Toggle k="show_level" label="Show my Level Up level" testid="public-toggle-level" />
        <Toggle k="show_streaks" label="Show my streak badges" testid="public-toggle-streaks" />
        <Toggle k="show_networth" label="Show my estimated lifetime earnings" testid="public-toggle-networth" />
      </div>
      {st.enabled && url && (
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="public-card-link">
          <code className="text-xs bg-white border border-sand rounded-md px-2 py-1.5 truncate max-w-full">{url}</code>
          <button onClick={() => { navigator.clipboard?.writeText(url); setMsg("Link copied."); }} className="inline-flex items-center gap-1 text-xs text-forest font-medium" data-testid="public-card-copy"><Copy className="w-3.5 h-3.5" /> Copy</button>
          <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-forest font-medium" data-testid="public-card-open"><ExternalLink className="w-3.5 h-3.5" /> Open</a>
        </div>
      )}
      {msg && <div className="text-xs text-forest mt-2">{msg}</div>}
      {err && <div className="text-xs text-clay mt-2" data-testid="public-card-error">{err}</div>}
    </div>
  );
}

export function Avatar({ user, size = 40, className = "" }) {
  const initials = (user?.name || user?.email || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return user?.avatar ? (
    <img src={user.avatar} alt={user.username || user.name || "avatar"} width={size} height={size}
      className={`rounded-full object-cover border border-sand ${className}`} style={{ width: size, height: size }} data-testid="avatar-img" />
  ) : (
    <span className={`rounded-full bg-forest text-white inline-flex items-center justify-center font-serif ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.4 }} data-testid="avatar-initials">{initials}</span>
  );
}

function resizeToDataUrl(file, max = 256) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const out = Math.min(max, side);
      const canvas = document.createElement("canvas");
      canvas.width = out; canvas.height = out;
      const sx = (img.naturalWidth - side) / 2, sy = (img.naturalHeight - side) / 2;
      canvas.getContext("2d").drawImage(img, sx, sy, side, side, 0, 0, out, out);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
    img.src = url;
  });
}

export default function ProfileIdentityCard() {
  const { user, refresh } = useAuth();
  const [username, setUsername] = useState(user?.username || "");
  const [name, setName] = useState(user?.name || "");
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    const u = username.trim().toLowerCase();
    if (!u || u === user?.username) { setAvail(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/auth/username-available/${encodeURIComponent(u)}`); setAvail(data); } catch { setAvail(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [username, user?.username]);

  const save = async (payload, ok) => {
    setBusy(true); setErr(""); setMsg("");
    try { await api.patch("/auth/profile", payload); await refresh(); setMsg(ok); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { setErr("Please choose an image file."); return; }
    try { const dataUrl = await resizeToDataUrl(f); await save({ avatar: dataUrl }, "Profile picture updated."); }
    catch { setErr("Couldn't read that image."); }
    finally { e.target.value = ""; }
  };

  return (
    <div className="mt-6 bg-linen border border-sand rounded-2xl p-8" data-testid="profile-identity">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Identity</div>
      <div className="mt-4 flex flex-col sm:flex-row gap-8">
        <div className="flex flex-col items-center gap-3 shrink-0">
          <Avatar user={user} size={112} />
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="avatar-file-input" />
          <div className="flex gap-2">
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="inline-flex items-center gap-1.5 bg-forest text-white rounded-md px-3 py-2 text-xs font-medium hover:bg-forest-deep disabled:opacity-50" data-testid="avatar-upload-btn">
              <Camera className="w-3.5 h-3.5" /> {user?.avatar ? "Change photo" : "Add photo"}
            </button>
            {user?.avatar && (
              <button onClick={() => save({ avatar: "" }, "Profile picture removed.")} disabled={busy}
                className="inline-flex items-center gap-1 border border-sand rounded-md px-3 py-2 text-xs text-clay hover:bg-clay-light" data-testid="avatar-remove-btn">
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>
          <div className="text-[10px] text-sage text-center">JPG/PNG · auto-cropped to a 256px square</div>
        </div>

        <div className="flex-1 space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-sage">Username</label>
            <div className="mt-2 relative">
              <AtSign className="w-4 h-4 text-sage absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24))}
                placeholder="skye_levelsup" className="w-full bg-white border border-sand rounded-lg pl-9 pr-3 py-3 font-mono text-sm focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                data-testid="username-input" />
            </div>
            <div className="mt-1.5 text-[11px] min-h-[16px]" data-testid="username-availability">
              {avail ? (avail.available
                ? <span className="text-forest inline-flex items-center gap-1"><Check className="w-3 h-3" /> @{avail.username} is available</span>
                : <span className="text-clay inline-flex items-center gap-1"><X className="w-3 h-3" /> {avail.reason}</span>)
                : <span className="text-sage">3–24 characters · lowercase letters, numbers, underscores{user?.username ? ` · currently @${user.username}` : ""}</span>}
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-sage">Display name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
              className="mt-2 w-full bg-white border border-sand rounded-lg px-3 py-3 text-sm focus:ring-2 focus:ring-forest focus:border-transparent outline-none" data-testid="display-name-input" />
          </div>
          <button onClick={() => save({ username: username.trim(), name }, "Profile saved.")}
            disabled={busy || (avail && !avail.available) || (!username.trim() && !name.trim())}
            className="bg-ink text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-ink-soft disabled:opacity-50" data-testid="profile-save-btn">
            {busy ? "Saving…" : "Save profile"}
          </button>
          {msg && <div className="text-sm text-forest" data-testid="profile-msg">{msg}</div>}
          {err && <div className="text-sm text-clay" data-testid="profile-error">{err}</div>}
        </div>
      </div>
      <PublicCardSettings user={user} />
    </div>
  );
}
